import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { TextField } from '@mui/material';
import { PartidaLite } from '../../../../services/faseGrupoPartidasService';
import { criarPartida, atualizarPartida, PartidaInput, partidaPorId } from '../../../../services/partidaService';
import EditMatchFormatDialog from '../../../../components/match/EditMatchFormatDialog';
import { buscarEquipesPorCategoriaTorneio } from '../../../../services/equipeService';
import { buscarCategoriaTorneioPorId, CategoriaTorneio } from '../../../../services/categoriaTorneioService';
import { Equipe } from '../../../../types/equipe';
import { SetPlacar, normalizeSets, calculateFinalResults } from '../../../../types/placar';
import LocalModal, { LocalModalForm } from '../../../../components/admin/LocalModal';
import ScoreboardGrid from '../../../../components/match/ScoreboardGrid';
import { BeachTennisScoreValidator, ValidacaoPlacarResult, PartidaMinima } from '../../../../utils/beachTennisScoreValidator';
import { upsertLocal, LocalInput, getLocalById, LocalDTO } from '../../../../services/localService';
import TeamSelectorDialog from '../../../../components/tournament/TeamSelectorDialog';
import AthleteSelectorDialog from '../../../../components/tournament/AthleteSelectorDialog';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Atleta selecionável para os slots de participante no rodízio (pool do grupo).
export interface AtletaSelecionavel {
  id: string;
  usuario?: { id: string; nome?: string; imagemUrl?: string | null } | null;
}

interface CreateEditMatchModalProps {
  partida?: PartidaLite | null;
  categoriaId: string;
  faseCategoriaTorneioId: string;
  grupoId?: string;
  onClose: () => void;
  onSuccess: () => void;
  canEdit?: boolean;
  /** Pool de atletas do grupo — habilita a edição manual dos participantes no rodízio. */
  atletasDoGrupo?: AtletaSelecionavel[];
}

const CreateEditMatchModal: React.FC<CreateEditMatchModalProps> = ({
  partida,
  categoriaId,
  faseCategoriaTorneioId,
  grupoId,
  onClose,
  onSuccess,
  canEdit = true,
  atletasDoGrupo,
}) => {
  const [categoria, setCategoria] = useState<CategoriaTorneio | null>(null);
  const [equipes, setEquipes] = useState<Equipe[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<Date | null>(null);
  const [equipeAId, setEquipeAId] = useState<string>('');
  const [equipeBId, setEquipeBId] = useState<string>('');
  // Rodízio: ids dos atletas de cada lado (1 por slot), editáveis pelo organizador.
  const [participantesAIds, setParticipantesAIds] = useState<string[]>([]);
  const [participantesBIds, setParticipantesBIds] = useState<string[]>([]);
  const [localId, setLocalId] = useState<string | null>(null);
  const [usarLocalTorneio, setUsarLocalTorneio] = useState(false);
  const [local, setLocal] = useState<LocalDTO | null>(null);
  const [sets, setSets] = useState<SetPlacar[]>([]);
  const [resultadoFinalA, setResultadoFinalA] = useState<number | null>(null);
  const [resultadoFinalB, setResultadoFinalB] = useState<number | null>(null);
  const [equipeAWO, setEquipeAWO] = useState(false);
  const [equipeBWO, setEquipeBWO] = useState(false);
  const [equipeADesistencia, setEquipeADesistencia] = useState(false);
  const [equipeBDesistencia, setEquipeBDesistencia] = useState(false);

  // Estados para modais aninhados
  const [localModalOpen, setLocalModalOpen] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [teamSelectorOpen, setTeamSelectorOpen] = useState(false);
  const [teamSelectorEquipe, setTeamSelectorEquipe] = useState<'A' | 'B' | null>(null);
  const [teamSelectorBusca, setTeamSelectorBusca] = useState('');
  // Rodízio: slot de participante em edição (abre o AthleteSelectorDialog) + busca do seletor.
  const [athleteSelectorSlot, setAthleteSelectorSlot] = useState<{ lado: 'A' | 'B'; idx: number } | null>(null);
  const [athleteSelectorBusca, setAthleteSelectorBusca] = useState('');
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [editFormatDialogOpen, setEditFormatDialogOpen] = useState(false);
  const [partidaData, setPartidaData] = useState<{
    numeroSets?: number | null;
    gamesPorSets?: number | null;
    tiebreaks?: number | null;
    placar?: any;
  } | null>(null);
  const [validacaoPlacar, setValidacaoPlacar] = useState<ValidacaoPlacarResult | null>(null);
  const [placarError, setPlacarError] = useState<string | null>(null);

  const loadCategoria = useCallback(async () => {
    try {
      const cat = await buscarCategoriaTorneioPorId(categoriaId);
      setCategoria(cat);
    } catch (error) {
      console.error('Erro ao carregar categoria:', error);
    }
  }, [categoriaId]);

  const loadEquipes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await buscarEquipesPorCategoriaTorneio(categoriaId);
      setEquipes(data);
    } catch (error) {
      console.error('Erro ao carregar equipes:', error);
      alert('Erro ao carregar equipes');
    } finally {
      setLoading(false);
    }
  }, [categoriaId]);

  const loadPartidaCompleta = useCallback(async () => {
    if (!partida?.id) return;
    try {
      const partidaCompleta = await partidaPorId(partida.id);
      // Armazenar dados da partida para uso no dialog
      setPartidaData({
        numeroSets: partidaCompleta.numeroSets,
        gamesPorSets: partidaCompleta.gamesPorSets,
        tiebreaks: partidaCompleta.tiebreaks,
        placar: partidaCompleta.placar,
      });
      // Verificar se o local já está completo na resposta (endereco, cidade e
      // estado são opcionais no schema — se faltar algo, buscar via getLocalById)
      if (partidaCompleta.local?.id && partidaCompleta.local.endereco?.cidade?.estado) {
        // Se o local já tem endereco completo, usar diretamente
        setLocal({
          id: partidaCompleta.local.id,
          nome: partidaCompleta.local.nome,
          descricao: null,
          endereco: {
            id: partidaCompleta.local.endereco.id,
            cep: partidaCompleta.local.endereco.cep,
            logradouro: partidaCompleta.local.endereco.logradouro,
            numero: partidaCompleta.local.endereco.numero,
            complemento: partidaCompleta.local.endereco.complemento,
            bairro: partidaCompleta.local.endereco.bairro,
            cidade: {
              id: partidaCompleta.local.endereco.cidade.id,
              nome: partidaCompleta.local.endereco.cidade.nome,
              uf: partidaCompleta.local.endereco.cidade.estado.uf,
            },
          },
        });
        setLocalId(partidaCompleta.local.id);
        setUsarLocalTorneio(false);
      } else if (partidaCompleta.local?.id) {
        // Se só tem o ID, buscar dados completos
        const localData = await getLocalById(partidaCompleta.local.id);
        setLocal(localData);
        setLocalId(localData.id);
        setUsarLocalTorneio(false);
      } else {
        setUsarLocalTorneio(true);
        setLocalId(null);
      }
    } catch (error) {
      console.error('Erro ao carregar partida completa:', error);
    }
  }, [partida?.id]);

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      await Promise.all([loadCategoria(), loadEquipes()]);
      if (partida) {
        await loadPartidaCompleta();
      }
      setInitialLoading(false);
    };
    loadInitialData();
  }, [partida, loadCategoria, loadEquipes, loadPartidaCompleta]);

  useEffect(() => {
    if (partida) {
      // Validar data antes de criar objeto Date
      const partidaDate = partida.data 
        ? (isNaN(new Date(partida.data).getTime()) ? null : new Date(partida.data))
        : null;
      setData(partidaDate);
      setEquipeAId(partida.equipeA?.id || '');
      setEquipeBId(partida.equipeB?.id || '');
      setParticipantesAIds((partida.participantesA ?? []).map((a) => a.id));
      setParticipantesBIds((partida.participantesB ?? []).map((a) => a.id));
      setEquipeAWO(partida.equipeAWO || false);
      setEquipeBWO(partida.equipeBWO || false);
      setEquipeADesistencia(partida.equipeADesistencia || false);
      setEquipeBDesistencia(partida.equipeBDesistencia || false);
      if (partida.placar) {
        setResultadoFinalA(partida.placar.resultadoFinalA ?? null);
        setResultadoFinalB(partida.placar.resultadoFinalB ?? null);
      }
    } else {
      setParticipantesAIds([]);
      setParticipantesBIds([]);
      setEquipeAWO(false);
      setEquipeBWO(false);
      setEquipeADesistencia(false);
      setEquipeBDesistencia(false);
      setResultadoFinalA(null);
      setResultadoFinalB(null);
      setUsarLocalTorneio(true);
    }
  }, [partida]);

  useEffect(() => {
    if (categoria) {
      const numeroSets = partida?.numeroSets ?? categoria.numeroSets ?? 3;
      if (partida?.placar) {
        const normalizedSets = normalizeSets(partida.placar.sets, numeroSets);
        setSets(normalizedSets);
      } else {
        // Sempre garantir o número correto de sets baseado na categoria
        const initialSets = Array.from({ length: numeroSets }, (_, i) => ({
          numeroSet: i + 1,
          gamesA: null,
          gamesB: null,
          tiebreakA: null,
          tiebreakB: null,
        }));
        setSets(initialSets);
      }
    }
  }, [categoria, partida?.placar]);

  // Recalcular resultado final sempre que os sets mudarem
  useEffect(() => {
    const { resultadoFinalA: calcA, resultadoFinalB: calcB } = calculateFinalResults(sets);
    setResultadoFinalA(calcA);
    setResultadoFinalB(calcB);
  }, [sets]);

  // Validar placar quando sets ou categoria mudarem
  useEffect(() => {
    const validarPlacarAtual = () => {
      if (!categoria) return;

      const partidaParaValidar: PartidaMinima = {
        categoriaTorneio: {
          numeroSets: categoria.numeroSets ?? null,
          gamesPorSets: categoria.gamesPorSets ?? null,
          tiebreaks: categoria.tiebreaks ?? null,
        },
        numeroSets: partidaData?.numeroSets ?? null,
        gamesPorSets: partidaData?.gamesPorSets ?? null,
        tiebreaks: partidaData?.tiebreaks ?? null,
        placar: {
          sets: sets,
        },
        // WO/desistência: o placar parcial pode estar empatado — pula a regra de vencer por 2.
        permitirEmpateParcial:
          equipeAWO || equipeBWO || equipeADesistencia || equipeBDesistencia,
      };

      try {
        const resultado = BeachTennisScoreValidator.validarPlacar(partidaParaValidar);
        setValidacaoPlacar(resultado);
      } catch (error) {
        console.error('Erro ao validar placar:', error);
        setValidacaoPlacar(null);
      }
    };

    validarPlacarAtual();
  }, [sets, categoria, partidaData, equipeAWO, equipeBWO, equipeADesistencia, equipeBDesistencia]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !localModalOpen && !teamSelectorOpen && !athleteSelectorSlot && !datePickerOpen) {
        onClose();
      }
    };
    
    const handleClickOutside = (e: MouseEvent) => {
      if (datePickerOpen && datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setDatePickerOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.classList.remove('modal-open');
    };
  }, [onClose, localModalOpen, teamSelectorOpen, athleteSelectorSlot, datePickerOpen]);

  const formatDateForBackend = (date: Date | null): string | null => {
    if (!date) return null;
    // Formato RFC3339 completo para DateTime
    return date.toISOString();
  };

  // Rodízio (Rei/Rainha, Super 8): os lados são 2 atletas avulsos (participantes),
  // não equipes — não há seleção de equipe e os participantes são fixos por rodada.
  const isRodizio =
    categoria?.modeloDisputa === 'REI_RAINHA' ||
    categoria?.modeloDisputa === 'SUPER_8' ||
    (partida?.participantesA?.length ?? 0) > 0 ||
    (partida?.participantesB?.length ?? 0) > 0;

  // A edição manual dos participantes é habilitada quando há partida (edição), a fase não está
  // finalizada (canEdit) e o pool de atletas do grupo foi fornecido pelo componente pai. Uma
  // partida já FINALIZADA também pode ter os participantes editados (correção pelo organizador).
  const participantesEditaveis =
    isRodizio && !!partida && canEdit && (atletasDoGrupo?.length ?? 0) > 0;

  // Lookup id→atleta para exibir nome/foto do participante de cada slot (pool do grupo +
  // participantes originais da partida, caso algum não esteja no pool).
  const atletaPorId = useMemo(() => {
    const mapa = new Map<string, AtletaSelecionavel>();
    (atletasDoGrupo ?? []).forEach((a) => mapa.set(a.id, a));
    (partida?.participantesA ?? []).forEach((a) => mapa.set(a.id, a as AtletaSelecionavel));
    (partida?.participantesB ?? []).forEach((a) => mapa.set(a.id, a as AtletaSelecionavel));
    return mapa;
  }, [atletasDoGrupo, partida]);

  const abrirSeletorAtleta = (lado: 'A' | 'B', idx: number) => {
    setAthleteSelectorBusca('');
    setAthleteSelectorSlot({ lado, idx });
  };

  const handleAthleteSelect = (atleta: { id: string }) => {
    if (!athleteSelectorSlot) return;
    const { lado, idx } = athleteSelectorSlot;
    const setter = lado === 'A' ? setParticipantesAIds : setParticipantesBIds;
    setter((prev) => prev.map((v, i) => (i === idx ? atleta.id : v)));
    setAthleteSelectorSlot(null);
    setAthleteSelectorBusca('');
  };

  // Ids já usados na partida, exceto o do slot em edição → desabilitados no seletor
  // (evita atleta repetido no mesmo lado ou nos dois lados).
  const idsUsadosExcetoSlot = (slot: { lado: 'A' | 'B'; idx: number }): string[] => {
    const atual = slot.lado === 'A' ? participantesAIds[slot.idx] : participantesBIds[slot.idx];
    return [...participantesAIds, ...participantesBIds].filter((id) => id !== atual);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que ambas as equipes estão selecionadas e são diferentes.
    // No rodízio não há equipes (lados = participantes fixos) — pular a validação.
    if (!isRodizio && (!equipeAId || !equipeBId)) {
      alert('Por favor, selecione ambas as equipes');
      return;
    }
    if (!isRodizio && equipeAId === equipeBId) {
      alert('As equipes devem ser diferentes');
      return;
    }

    // Validar edição manual dos participantes (rodízio): slots preenchidos, sem repetição
    // no mesmo lado e sem o mesmo atleta nos dois lados.
    if (participantesEditaveis) {
      const todos = [...participantesAIds, ...participantesBIds];
      if (todos.some((id) => !id)) {
        alert('Selecione todos os atletas de ambos os lados.');
        return;
      }
      const setA = new Set(participantesAIds);
      const setB = new Set(participantesBIds);
      if (setA.size !== participantesAIds.length || setB.size !== participantesBIds.length) {
        alert('Um atleta não pode aparecer duas vezes no mesmo lado da partida.');
        return;
      }
      if (participantesAIds.some((id) => setB.has(id))) {
        alert('Um atleta não pode participar dos dois lados da mesma partida.');
        return;
      }
    }

    // Validar placar antes de salvar
    if (validacaoPlacar && !validacaoPlacar.valido) {
      setPlacarError(validacaoPlacar.mensagem || 'O placar contém erros. Verifique os valores preenchidos.');
      return;
    }

    setSaving(true);
    setPlacarError(null);
    try {
      const setsLimpos = sets.map((set: any) => {
        const { __typename, ...rest } = set;
        return rest;
      });
      
      const temWO = equipeAWO || equipeBWO;
      const placarInput = temWO ? null : {
        sets: setsLimpos,
        resultadoFinalA: resultadoFinalA,
        resultadoFinalB: resultadoFinalB,
      };

      const finalLocalId = usarLocalTorneio ? null : localId;

      if (partida) {
        const partidaInput: PartidaInput = {
          data: formatDateForBackend(data),
          localId: finalLocalId,
          categoriaTorneioId: categoriaId,
          faseCategoriaTorneioId: faseCategoriaTorneioId,
          faseGrupoPartidasId: grupoId || null,
          equipeAId: equipeAId || null,
          equipeBId: equipeBId || null,
          status: partida.status || null,
          equipeAWO: equipeAWO || null,
          equipeBWO: equipeBWO || null,
          equipeADesistencia: equipeADesistencia || null,
          equipeBDesistencia: equipeBDesistencia || null,
          placar: placarInput,
        };
        // Envia os participantes só quando o organizador de fato alterou algum lado —
        // evita reescrever os PartidaParticipante em cada salvamento de placar.
        if (participantesEditaveis) {
          const origA = (partida.participantesA ?? []).map((a) => a.id);
          const origB = (partida.participantesB ?? []).map((a) => a.id);
          if (JSON.stringify(participantesAIds) !== JSON.stringify(origA)) {
            partidaInput.participantesAIds = participantesAIds;
          }
          if (JSON.stringify(participantesBIds) !== JSON.stringify(origB)) {
            partidaInput.participantesBIds = participantesBIds;
          }
        }
        await atualizarPartida(partida.id, partidaInput);
      } else {
        await criarPartida({
          data: formatDateForBackend(data),
          categoriaTorneioId: categoriaId,
          faseCategoriaTorneioId: faseCategoriaTorneioId,
          faseGrupoPartidasId: grupoId,
          equipeAId: equipeAId || undefined,
          equipeBId: equipeBId || undefined,
          localId: finalLocalId || undefined,
          status: undefined,
          equipeAWO: equipeAWO || null,
          equipeBWO: equipeBWO || null,
          equipeADesistencia: equipeADesistencia || null,
          equipeBDesistencia: equipeBDesistencia || null,
          placar: placarInput,
        });
      }
      onSuccess();
    } catch (error) {
      console.error('Erro ao salvar partida:', error);
      alert('Erro ao salvar partida');
    } finally {
      setSaving(false);
    }
  };

  const handleLocalSave = async (localData: LocalModalForm) => {
    setSavingLocal(true);
    try {
      const localInput: LocalInput = {
        nome: localData.nome,
        descricao: localData.descricao || null,
        urlLocalizacao: localData.urlLocalizacao,
        endereco: {
          cep: localData.cep,
          logradouro: localData.logradouro,
          numero: localData.numero,
          bairro: localData.bairro,
          complemento: localData.complemento || null,
          cidadeId: localData.cidadeId,
        },
      };
      const savedLocal = await upsertLocal({
        id: localId || undefined,
        local: localInput,
      });
      setLocal(savedLocal);
      setLocalId(savedLocal.id);
      setUsarLocalTorneio(false);
      setLocalModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar local:', error);
      alert('Erro ao salvar local');
    } finally {
      setSavingLocal(false);
    }
  };

  const handleTeamSelect = (equipe: Equipe) => {
    if (teamSelectorEquipe === 'A') {
      setEquipeAId(equipe.id);
    } else if (teamSelectorEquipe === 'B') {
      setEquipeBId(equipe.id);
    }
    setTeamSelectorOpen(false);
    setTeamSelectorEquipe(null);
    setTeamSelectorBusca('');
  };

  const handleSetFieldUpdate = (numeroSet: number, campo: 'gamesA' | 'gamesB' | 'tiebreakA' | 'tiebreakB', value: number | null) => {
    setSets(prevSets => {
      const newSets = [...prevSets];
      const index = newSets.findIndex(s => s.numeroSet === numeroSet);
      if (index === -1) return prevSets;
      
      const updatedSet = {
        ...newSets[index],
        [campo]: value,
      };
      
      // Se estamos atualizando gamesA ou gamesB, verificar se o set ainda precisa de tiebreak
      // Se não precisar, limpar tiebreakA e tiebreakB
      if (campo === 'gamesA' || campo === 'gamesB') {
        const gamesPorSets = partidaData?.gamesPorSets ?? categoria?.gamesPorSets ?? null;
        const tiebreaks = partidaData?.tiebreaks ?? categoria?.tiebreaks ?? null;
        
        const deveTerTiebreak = BeachTennisScoreValidator.deveTerTiebreak(
          updatedSet,
          gamesPorSets,
          tiebreaks
        );
        
        if (!deveTerTiebreak) {
          updatedSet.tiebreakA = null;
          updatedSet.tiebreakB = null;
        }
      }
      
      newSets[index] = updatedSet;
      return newSets;
    });
  };

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !localModalOpen && !teamSelectorOpen && !athleteSelectorSlot) {
      onClose();
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return 'Data não definida';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getLocalDisplay = () => {
    if (usarLocalTorneio && categoria?.torneio?.local) {
      // No schema, Local.endereco e Endereco.cidade são opcionais — torneio
      // criado via API pode ter local sem endereço.
      const end = categoria.torneio.local.endereco;
      if (end) {
        return {
          nome: 'Local do torneio',
          endereco: `${end.logradouro}, ${end.numero}${end.complemento ? ` - ${end.complemento}` : ''}, ${end.bairro}${end.cidade ? `, ${end.cidade.nome}/${end.cidade.uf}` : ''}`,
        };
      }
      return {
        nome: 'Local do torneio',
        endereco: 'Endereço não informado',
      };
    }
    if (local) {
      const end = local.endereco;
      if (end) {
        return {
          nome: local.nome,
          endereco: `${end.logradouro}, ${end.numero}${end.complemento ? ` - ${end.complemento}` : ''}, ${end.bairro}${end.cidade ? `, ${end.cidade.nome}/${end.cidade.uf}` : ''}`,
        };
      }
      return {
        nome: local.nome,
        endereco: 'Endereço não informado',
      };
    }
    return {
      nome: 'Local não definido',
      endereco: '',
    };
  };

  // Rodízio: exibe o NOME de cada participante do lado. Clicar no nome abre o
  // AthleteSelectorDialog quando editável (inclusive em partida finalizada); caso
  // contrário (fase finalizada / sem pool) é apenas leitura.
  const renderNomeSlot = (lado: 'A' | 'B', idx: number, nome: string) => {
    if (participantesEditaveis) {
      return (
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none text-start"
          aria-label={`Editar ${lado === 'A' ? 'Lado A' : 'Lado B'} - atleta ${idx + 1}`}
          onClick={() => abrirSeletorAtleta(lado, idx)}
        >
          {nome}
        </button>
      );
    }
    return <span>{nome}</span>;
  };

  const renderLadoRodizio = (lado: 'A' | 'B') => {
    const ids = lado === 'A' ? participantesAIds : participantesBIds;
    if (ids.length === 0) {
      return <div className="text-muted">Sem atletas</div>;
    }
    return (
      <div>
        {ids.map((id, idx) => {
          const atleta = atletaPorId.get(id);
          const nome = atleta?.usuario?.nome || 'Atleta';
          return (
            <div key={`${lado}-${idx}`} className="d-flex align-items-center mb-2">
              <img
                src={atleta?.usuario?.imagemUrl || '/assets/images/user.png'}
                alt={nome}
                className="rounded-circle me-2"
                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
              />
              {renderNomeSlot(lado, idx, nome)}
            </div>
          );
        })}
      </div>
    );
  };

  const equipeA = equipes.find(e => e.id === equipeAId);
  const equipeB = equipes.find(e => e.id === equipeBId);
  const localDisplay = getLocalDisplay();
  const numeroSets = partidaData?.numeroSets ?? categoria?.numeroSets ?? 3;

  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose} />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div 
          className="modal-dialog modal-dialog-centered modal-lg" 
          role="document" 
          onMouseDown={(e) => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '90vh' }}
        >
          <form 
            className={`modal-content ${localModalOpen ? 'opacity-50' : ''}`}
            onSubmit={handleSubmit}
            style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <h5 className="modal-title">
                {partida ? 'Editar Partida' : 'Criar Partida'}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              ></button>
            </div>
            <div 
              className="modal-body"
              style={{ overflowY: 'auto', flex: 1 }}
            >
              {initialLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              ) : (
                <>
              {/* Mensagem de fase finalizada */}
              {!canEdit && partida && (
                <div className="alert alert-info mb-3" role="alert">
                  <strong>Edição desabilitada.</strong> Esta fase já está finalizada.
                  Para editar as partidas desta fase, altere o status da fase para
                  "Agendada" ou "Em Andamento" no painel de edição da fase.
                </div>
              )}

              {/* Seção 1: Local e Data */}
              <div className="mb-2 d-flex space-between row">
                <div className="col-6">
                  <TextField
                    label="Local"
                    value={`${localDisplay.nome}${localDisplay.endereco ? ` - ${localDisplay.endereco}` : ''}`}
                    onClick={() => canEdit && setLocalModalOpen(true)}
                    slotProps={{
                      input: {
                        readOnly: true
                      }
                    }}
                    sx={{
                      cursor: canEdit ? 'pointer' : 'default',
                      '& .MuiInputBase-input': {
                        cursor: canEdit ? 'pointer' : 'default'
                      }
                    }}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </div>
                <div className="col-6">
                  {canEdit ? (
                    <div style={{ position: 'relative' }} ref={datePickerRef}>
                      <TextField
                        label="Data e Horário"
                        value={formatDate(data)}
                        onClick={() => setDatePickerOpen(!datePickerOpen)}
                        slotProps={{
                          input: {
                            readOnly: true
                          }
                        }}
                        sx={{
                          cursor: 'pointer',
                          '& .MuiInputBase-input': {
                            cursor: 'pointer'
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        size="small"
                      />
                      {datePickerOpen && (
                        <div style={{ position: 'absolute', zIndex: 1050, top: '100%', left: 0, marginTop: '4px', backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                          <DatePicker
                            selected={data}
                            onChange={(date: Date | null) => {
                              setData(date);
                              setDatePickerOpen(false);
                            }}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="dd/MM/yyyy HH:mm"
                            inline
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <TextField
                      label="Data e Horário"
                      value={formatDate(data)}
                      slotProps={{ input: { readOnly: true } }}
                      fullWidth
                      variant="outlined"
                      size="small"
                    />
                  )}
                </div>
              </div>

              {/* Seção 2: Equipes e Resultado Final */}
              <div className="mb-4">
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label small text-muted">{isRodizio ? 'Lado A' : 'Equipe A'}</label>
                    <div
                      className={`p-3 border rounded ${!isRodizio && canEdit ? 'cursor-pointer' : ''}`}
                      style={{ cursor: !isRodizio && canEdit ? 'pointer' : 'default', minHeight: '100px' }}
                      onClick={() => !isRodizio && canEdit && (setTeamSelectorEquipe('A'), setTeamSelectorOpen(true))}
                    >
                      {isRodizio ? (
                        renderLadoRodizio('A')
                      ) : equipeA ? (
                        <div>
                          {equipeA.atletas.map((atleta) => (
                            <div key={atleta.id} className="d-flex align-items-center mb-2">
                              <img
                                src={atleta.usuario?.imagemUrl || '/assets/images/user.png'}
                                alt={atleta.usuario?.nome || 'Atleta'}
                                className="rounded-circle me-2"
                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              />
                              <span>{atleta.usuario?.nome || 'Atleta'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted">Selecione a equipe A</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-2 d-flex flex-column justify-content-center align-items-center">
                    {!equipeAWO && !equipeBWO && (
                      <>
                        <div className="text-muted small">Resultado</div>
                        <div className="fw-bold h1 ">
                          {resultadoFinalA !== null && resultadoFinalB !== null
                            ? `${resultadoFinalA} x ${resultadoFinalB}`
                            : '-'}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="col-md-5">
                    <label className="form-label small text-muted">{isRodizio ? 'Lado B' : 'Equipe B'}</label>
                    <div
                      className={`p-3 border rounded ${!isRodizio && canEdit ? 'cursor-pointer' : ''}`}
                      style={{ cursor: !isRodizio && canEdit ? 'pointer' : 'default', minHeight: '100px' }}
                      onClick={() => !isRodizio && canEdit && (setTeamSelectorEquipe('B'), setTeamSelectorOpen(true))}
                    >
                      {isRodizio ? (
                        renderLadoRodizio('B')
                      ) : equipeB ? (
                        <div>
                          {equipeB.atletas.map((atleta) => (
                            <div key={atleta.id} className="d-flex align-items-center mb-2">
                              <img
                                src={atleta.usuario?.imagemUrl || '/assets/images/user.png'}
                                alt={atleta.usuario?.nome || 'Atleta'}
                                className="rounded-circle me-2"
                                style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                              />
                              <span>{atleta.usuario?.nome || 'Atleta'}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-muted">Selecione a equipe B</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Seção 3: Placar Completo */}
              {!equipeAWO && !equipeBWO && (
                <>
                  {placarError && (
                    <div className="alert alert-danger mb-3" role="alert" style={{ whiteSpace: 'pre-line' }}>
                      {placarError}
                    </div>
                  )}
                  <ScoreboardGrid
                    sets={sets}
                    numeroSets={numeroSets}
                    gamesPorSets={partidaData?.gamesPorSets}
                    tiebreaks={partidaData?.tiebreaks}
                    categoriaTorneio={categoria ? {
                      numeroSets: categoria.numeroSets ?? null,
                      gamesPorSets: categoria.gamesPorSets ?? null,
                      tiebreaks: categoria.tiebreaks ?? null,
                    } : null}
                    canEdit={canEdit}
                    onSetFieldUpdate={handleSetFieldUpdate}
                    camposInvalidos={validacaoPlacar?.camposInvalidos}
                  />
                </>
              )}

              {/* Seção de WO e Desistência */}
              {canEdit && (
                <div className="mb-3">
                  <h6 className="mb-3 fw-bold">WO e Desistência</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          id="equipe-a-wo"
                          checked={equipeAWO}
                          onChange={(e) => {
                            setEquipeAWO(e.target.checked);
                            if (e.target.checked) setEquipeADesistencia(false);
                          }}
                          className="form-check-input"
                        />
                        <label htmlFor="equipe-a-wo" className="form-check-label">
                          Equipe A - WO
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="equipe-a-desistencia"
                          checked={equipeADesistencia}
                          onChange={(e) => {
                            setEquipeADesistencia(e.target.checked);
                            if (e.target.checked) setEquipeAWO(false);
                          }}
                          className="form-check-input"
                        />
                        <label htmlFor="equipe-a-desistencia" className="form-check-label">
                          Equipe A - Desistência
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check mb-2">
                        <input
                          type="checkbox"
                          id="equipe-b-wo"
                          checked={equipeBWO}
                          onChange={(e) => {
                            setEquipeBWO(e.target.checked);
                            if (e.target.checked) setEquipeBDesistencia(false);
                          }}
                          className="form-check-input"
                        />
                        <label htmlFor="equipe-b-wo" className="form-check-label">
                          Equipe B - WO
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          id="equipe-b-desistencia"
                          checked={equipeBDesistencia}
                          onChange={(e) => {
                            setEquipeBDesistencia(e.target.checked);
                            if (e.target.checked) setEquipeBWO(false);
                          }}
                          className="form-check-input"
                        />
                        <label htmlFor="equipe-b-desistencia" className="form-check-label">
                          Equipe B - Desistência
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
                </>
              )}
            </div>
            {canEdit && (
              <div className="modal-footer">
                {partida?.id && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setEditFormatDialogOpen(true)}
                    disabled={saving || !partida?.id}
                  >
                    Alterar Formato
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || loading}>
                  {saving ? 'Salvando...' : partida ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* LocalModal */}
      {localModalOpen && (
        <LocalModal
          open={localModalOpen}
          initialValues={local ? {
            nome: local.nome,
            descricao: local.descricao || undefined,
            urlLocalizacao: local.urlLocalizacao || undefined,
            cep: local.endereco?.cep || '',
            logradouro: local.endereco?.logradouro || '',
            numero: local.endereco?.numero || '',
            bairro: local.endereco?.bairro || '',
            complemento: local.endereco?.complemento || undefined,
            uf: local.endereco?.cidade.uf || '',
            cidadeId: local.endereco?.cidade.id || '',
          } : undefined}
          saving={savingLocal}
          onSave={handleLocalSave}
          onClose={() => setLocalModalOpen(false)}
          localTorneio={categoria?.torneio?.local ? {
            id: categoria.torneio.local.id,
            nome: categoria.torneio.local.nome,
            endereco: categoria.torneio.local.endereco,
          } : undefined}
          onUseLocalTorneio={() => {
            setUsarLocalTorneio(true);
            setLocalId(null);
            setLocal(null);
            setLocalModalOpen(false);
          }}
          onUnuseLocalTorneio={() => {
            setUsarLocalTorneio(false);
          }}
          usarLocalTorneio={usarLocalTorneio}
        />
      )}

      {/* TeamSelectorDialog */}
      {teamSelectorOpen && (
        <TeamSelectorDialog
          open={teamSelectorOpen}
          equipes={equipes}
          loading={loading}
          busca={teamSelectorBusca}
          onBuscaChange={setTeamSelectorBusca}
          onClose={() => {
            setTeamSelectorOpen(false);
            setTeamSelectorEquipe(null);
            setTeamSelectorBusca('');
          }}
          onSelect={handleTeamSelect}
          disabledTeamId={teamSelectorEquipe === 'A' ? equipeBId : equipeAId}
        />
      )}

      {/* AthleteSelectorDialog (rodízio) */}
      {athleteSelectorSlot && (
        <AthleteSelectorDialog
          open={!!athleteSelectorSlot}
          atletas={atletasDoGrupo ?? []}
          busca={athleteSelectorBusca}
          onBuscaChange={setAthleteSelectorBusca}
          onClose={() => {
            setAthleteSelectorSlot(null);
            setAthleteSelectorBusca('');
          }}
          onSelect={handleAthleteSelect}
          selectedAtletaId={
            athleteSelectorSlot.lado === 'A'
              ? participantesAIds[athleteSelectorSlot.idx]
              : participantesBIds[athleteSelectorSlot.idx]
          }
          disabledAtletaIds={idsUsadosExcetoSlot(athleteSelectorSlot)}
        />
      )}

      {/* EditMatchFormatDialog */}
      {editFormatDialogOpen && partida?.id && (
        <EditMatchFormatDialog
          open={editFormatDialogOpen}
          partidaId={partida.id}
          placarAtual={partidaData?.placar || partida.placar}
          numeroSets={partidaData?.numeroSets}
          gamesPorSets={partidaData?.gamesPorSets}
          tiebreaks={partidaData?.tiebreaks}
          onClose={() => {
            setEditFormatDialogOpen(false);
          }}
          onSuccess={async () => {
            setEditFormatDialogOpen(false);
            // Recarregar dados da partida após atualização
            if (partida?.id) {
              await loadPartidaCompleta();
            }
            onSuccess();
          }}
        />
      )}
    </>
  );
};

export default CreateEditMatchModal;
